 /**
  * =========================================================
  * CHADHAVA VARIANT SELECTION
  * =========================================================
  *
  * Handles:
  * - Multiple variant selection
  * - Quantity
  * - Live booking summary
  * - Total calculation
  * - Continue to Sankalp
  * - Selection change events
  */

(function () {

  'use strict';


  /* ==========================================
     INITIALIZE
  ========================================== */

  function initChadhavaVariants(section) {

    if (!section) {
      return;
    }


    const variantCards = Array.from(
      section.querySelectorAll('.variant-card')
    );


    const summary = section.querySelector(
      '[data-booking-summary]'
    );


    if (!summary) {
      return;
    }


    const summaryItems = summary.querySelector(
      '[data-summary-items]'
    );


    const emptyState = summary.querySelector(
      '[data-summary-empty]'
    );


    const totalElement = summary.querySelector(
      '[data-summary-total]'
    );


    const selectedCountElement = summary.querySelector(
      '[data-selected-count]'
    );


    const continueButton = summary.querySelector(
      '[data-continue-sankalp]'
    );


    /*
     * Selected variants are stored here.
     *
     * Example:
     *
     * {
     *   "123456": {
     *      id: 123456,
     *      title: "Seeds Worth ₹151",
     *      price: 15100,
     *      quantity: 2
     *   }
     * }
     */

    const selectedVariants = {};


    /* ==========================================
       PRODUCT DATA
    ========================================== */

    const variantDataElement = document.getElementById(
      'ChadhavaVariantData-' +
      section.dataset.sectionId
    );


    let productData = null;


    if (variantDataElement) {

      try {

        productData = JSON.parse(
          variantDataElement.textContent
        );

      } catch (error) {

        console.error(
          'Chadhava: Unable to read variant data.',
          error
        );

      }

    }


    /* ==========================================
       MONEY FORMAT
    ========================================== */

    function formatMoney(cents) {

      const amount = cents / 100;


      /*
       * Shopify's currency can vary by store.
       * We use Intl when available.
       */

      const currency =
        productData?.currency || 'INR';


      try {

        return new Intl.NumberFormat(
          document.documentElement.lang || 'en-IN',
          {
            style: 'currency',
            currency: currency,
            maximumFractionDigits: 2
          }
        ).format(amount);

      } catch (error) {

        return '₹' + amount.toFixed(2);

      }

    }


    /* ==========================================
       DISPATCH SELECTION CHANGE
    ========================================== */

    function dispatchSelectionChanged() {

      const items =
        Object.values(
          selectedVariants
        );


      document.dispatchEvent(
        new CustomEvent(
          'chadhava:selectionChanged',
          {
            detail: {
              items: items
            }
          }
        )
      );

    }


    /* ==========================================
       ADD VARIANT
    ========================================== */

    function addVariant(card) {

      const variantId = String(
        card.dataset.variantId
      );

      const price = Number(
        card.dataset.price
      );

      const title =
        card.dataset.title ||
        'Chadhava';


      if (!variantId) {
        return;
      }


      /*
      * Get selected purchase option
      */

      const selectedPurchaseOption =
        card.querySelector(
          '[data-purchase-option]:checked'
        );


      const sellingPlanId =
        selectedPurchaseOption?.dataset.sellingPlanId ||
        null;


      const sellingPlanName =
        selectedPurchaseOption?.dataset.sellingPlanName ||
        null;


      const purchaseType =
        selectedPurchaseOption?.dataset.purchaseType ||
        'one-time';


      /*
      * If already selected,
      * increase quantity.
      *
      * IMPORTANT:
      * Do not change purchase type
      * when simply increasing quantity.
      */

      if (selectedVariants[variantId]) {

        selectedVariants[variantId].quantity += 1;

      } else {

        selectedVariants[variantId] = {

          id: Number(variantId),

          title: title,

          price: price,

          quantity: 1,

          sellingPlanId: sellingPlanId,

          sellingPlanName: sellingPlanName,

          purchaseType: purchaseType

        };

      }


      updateCard(card);

      renderSummary();

    }


    /* ==========================================
       REMOVE VARIANT
    ========================================== */

    function removeVariant(variantId) {

      variantId = String(variantId);


      if (!selectedVariants[variantId]) {
        return;
      }


      delete selectedVariants[variantId];


      const card = section.querySelector(
        '[data-variant-id="' +
        variantId +
        '"]'
      );


      if (card) {

        updateCard(card);

      }


      renderSummary();

    }


    /* ==========================================
       CHANGE QUANTITY
    ========================================== */

    function changeQuantity(
      variantId,
      change
    ) {

      variantId = String(variantId);


      const item =
        selectedVariants[variantId];


      if (!item) {
        return;
      }


      item.quantity += change;


      if (item.quantity <= 0) {

        removeVariant(variantId);

        return;

      }


      const card = section.querySelector(
        '[data-variant-id="' +
        variantId +
        '"]'
      );


      if (card) {

        updateCard(card);

      }


      renderSummary();

    }


    /* ==========================================
       UPDATE VARIANT CARD
    ========================================== */

    function updateCard(card) {

      const variantId =
        String(
          card.dataset.variantId
        );


      const addButton =
        card.querySelector(
          '.variant-add-btn'
        );


      const quantitySelector =
        card.querySelector(
          '.variant-qty-selector'
        );


      const quantityValue =
        card.querySelector(
          '.qty-value'
        );


      const selected =
        selectedVariants[variantId];


      if (!selected) {

        if (addButton) {

          addButton.hidden = false;

          addButton.textContent =
            '+ Add';

        }


        if (quantitySelector) {

          quantitySelector.hidden = true;

        }


        card.classList.remove(
          'is-selected'
        );


        return;

      }


      card.classList.add(
        'is-selected'
      );


      if (addButton) {

        addButton.hidden = true;

      }


      if (quantitySelector) {

        quantitySelector.hidden = false;

      }


      if (quantityValue) {

        quantityValue.textContent =
          selected.quantity;

      }

    }


    /* ==========================================
       RENDER SUMMARY
    ========================================== */

    function renderSummary() {

      const items =
        Object.values(
          selectedVariants
        );


      const totalQuantity =
        items.reduce(
          function (total, item) {

            return total +
              item.quantity;

          },
          0
        );


      const totalPrice =
        items.reduce(
          function (total, item) {

            return total +
              (
                item.price *
                item.quantity
              );

          },
          0
        );


      /* ----------------------------------------
         Count
      ---------------------------------------- */

      if (selectedCountElement) {

        selectedCountElement.textContent =
          totalQuantity;

      }


      /* ----------------------------------------
         Total
      ---------------------------------------- */

      if (totalElement) {

        totalElement.textContent =
          formatMoney(totalPrice);

      }


      /* ----------------------------------------
         Empty State
      ---------------------------------------- */

      if (emptyState) {

        emptyState.hidden =
          items.length > 0;

      }


      /* ----------------------------------------
         Continue Button
      ---------------------------------------- */

      const hasSelection =
        items.length > 0;


      if (continueButton) {

        continueButton.disabled =
          !hasSelection;

        continueButton.setAttribute(
          'aria-disabled',
          String(!hasSelection)
        );

      }


      /* ----------------------------------------
         Summary Items
      ---------------------------------------- */

      if (!summaryItems) {

        dispatchSelectionChanged();

        return;

      }


      summaryItems
        .querySelectorAll(
          '[data-summary-item]'
        )
        .forEach(function (element) {

          element.remove();

        });


      if (!items.length) {

        dispatchSelectionChanged();

        return;

      }


      items.forEach(function (item) {

        const row =
          document.createElement(
            'div'
          );


        row.className =
          'booking-summary-item';


        row.setAttribute(
          'data-summary-item',
          ''
        );


        row.dataset.variantId =
          item.id;


        row.innerHTML = `

          <div class="summary-item-info">

            <span class="summary-item-title">
              ${escapeHTML(item.title)}
            </span>

            ${
              item.sellingPlanName
                ? `
                  <span class="summary-item-subscription">
                    ${escapeHTML(item.sellingPlanName)}
                  </span>
                `
                : `
                  <span class="summary-item-subscription">
                    One-time
                  </span>
                `
            }

            <span class="summary-item-price">
              ${formatMoney(item.price)}
            </span>

          </div>


          <div class="summary-item-actions">

            <button
              type="button"
              class="summary-qty-minus"
              data-summary-minus
              data-variant-id="${item.id}"
              aria-label="Decrease ${escapeHTML(item.title)} quantity"
            >
              −
            </button>


            <span
              class="summary-qty"
              aria-live="polite"
            >
              ${item.quantity}
            </span>


            <button
              type="button"
              class="summary-qty-plus"
              data-summary-plus
              data-variant-id="${item.id}"
              aria-label="Increase ${escapeHTML(item.title)} quantity"
            >
              +
            </button>


            <button
              type="button"
              class="summary-remove"
              data-summary-remove
              data-variant-id="${item.id}"
              aria-label="Remove ${escapeHTML(item.title)}"
            >
              ×
            </button>

          </div>

        `;


        summaryItems.appendChild(
          row
        );

      });


      /*
       * Notify the rest of the Chadhava
       * product page that the selection
       * has changed.
       *
       * Used by:
       * - Mobile booking bar
       * - Other future UI components
       */

      dispatchSelectionChanged();

    }


    /* ==========================================
       ESCAPE HTML
    ========================================== */

    function escapeHTML(value) {

      const div =
        document.createElement(
          'div'
        );


      div.textContent =
        value || '';


      return div.innerHTML;

    }


    /* ==========================================
       VARIANT CARD EVENTS
    ========================================== */

    variantCards.forEach(
      function (card) {

        const addButton =
          card.querySelector(
            '.variant-add-btn'
          );


        const minusButton =
          card.querySelector(
            '.qty-minus'
          );


        const plusButton =
          card.querySelector(
            '.qty-plus'
          );


        const variantId =
          String(
            card.dataset.variantId
          );


        if (addButton) {

          addButton.addEventListener(
            'click',
            function () {

              addVariant(card);

            }
          );

        }


        if (minusButton) {

          minusButton.addEventListener(
            'click',
            function () {

              changeQuantity(
                variantId,
                -1
              );

            }
          );

        }


        if (plusButton) {

          plusButton.addEventListener(
            'click',
            function () {

              changeQuantity(
                variantId,
                1
              );

            }
          );

        }

        const purchaseOptions =
        card.querySelectorAll(
          '[data-purchase-option]'
        );


        purchaseOptions.forEach(
          function (option) {

            option.addEventListener(
              'change',
              function () {

                const variantId =
                  String(
                    card.dataset.variantId
                  );


                /*
                * If this variant has already
                * been added, update its plan.
                */

                if (
                  selectedVariants[variantId]
                ) {

                  selectedVariants[variantId].sellingPlanId =
                    option.dataset.sellingPlanId ||
                    null;


                  selectedVariants[variantId].sellingPlanName =
                    option.dataset.sellingPlanName ||
                    null;


                  selectedVariants[variantId].purchaseType =
                    option.dataset.purchaseType ||
                    'one-time';


                  /*
                  * For subscription selection,
                  * use Shopify's allocation price
                  * for display.
                  */

                  if (
                    option.dataset.purchaseType ===
                    'subscription'
                  ) {

                    selectedVariants[variantId].price =
                      Number(
                        option.dataset.sellingPlanPrice ||
                        card.dataset.price
                      );

                  } else {

                    selectedVariants[variantId].price =
                      Number(
                        card.dataset.price
                      );

                  }


                  renderSummary();

                }

              }
            );

          }
        );

      }
    );


    /* ==========================================
       SUMMARY EVENTS
    ========================================== */

    summary.addEventListener(
      'click',
      function (event) {

        const target =
          event.target;


        const minus =
          target.closest(
            '[data-summary-minus]'
          );


        const plus =
          target.closest(
            '[data-summary-plus]'
          );


        const remove =
          target.closest(
            '[data-summary-remove]'
          );


        if (minus) {

          changeQuantity(
            minus.dataset.variantId,
            -1
          );

          return;

        }


        if (plus) {

          changeQuantity(
            plus.dataset.variantId,
            1
          );

          return;

        }


        if (remove) {

          removeVariant(
            remove.dataset.variantId
          );

        }

      }
    );


    /* ==========================================
       CONTINUE TO SANKALP
    ========================================== */

    if (continueButton) {

      continueButton.addEventListener(
        'click',
        function () {

          const items =
            Object.values(
              selectedVariants
            );


          if (!items.length) {
            return;
          }


          /*
           * Send selected variants
           * to the Sankalp popup.
           *
           * sankalp-popup.liquid listens
           * for this event.
           */

          document.dispatchEvent(
            new CustomEvent(
              'chadhava:continueToSankalp',
              {
                detail: {

                  productId:
                    productData?.productId,

                  items:
                    items

                }

              }
            )
          );

        }
      );

    }


    /* ==========================================
       INITIAL STATE
    ========================================== */

    renderSummary();

  }


  /* ==========================================
     FIND SECTIONS
  ========================================== */

  function initializeAllSections() {

    document
      .querySelectorAll(
        '.chadhava-variants-section'
      )
      .forEach(
        function (section) {

          initChadhavaVariants(
            section
          );

        }
      );

  }


  /* ==========================================
     DOM READY
  ========================================== */

  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initializeAllSections
    );

  } else {

    initializeAllSections();

  }


  /* ==========================================
     SHOPIFY THEME EDITOR SUPPORT
  ========================================== */

  document.addEventListener(
    'shopify:section:load',
    function (event) {

      const section =
        event.target;


      if (
        section &&
        section.classList &&
        section.classList.contains(
          'chadhava-variants-section'
        )
      ) {

        initChadhavaVariants(
          section
        );

      }

    }
  );

})();